const API_BASE = '/api';

async function request(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('scribehaven_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options.headers as Record<string, string>) || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(data.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  auth: {
    register: (data: { username: string; email: string; password: string; isMature?: boolean }) =>
      request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
    login: (data: { email: string; password: string }) =>
      request('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    me: () => request('/auth/me'),
    verifyEmail: (token: string) => request(`/auth/verify-email?token=${token}`),
    resendVerification: () => request('/auth/resend-verification', { method: 'POST' }),
  },
  stories: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/stories${qs}`);
    },
    get: (id: number) => request(`/stories/${id}`),
    getBySlug: (slug: string) => request(`/stories/by-slug/${encodeURIComponent(slug)}`),
    create: (data: any) => request('/stories', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/stories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/stories/${id}`, { method: 'DELETE' }),
    follow: (id: number) => request(`/stories/${id}/follow`, { method: 'POST' }),
    readingList: (id: number, status: string | null) =>
      request(`/stories/${id}/reading-list`, { method: 'POST', body: JSON.stringify({ status }) }),
    homepage: () => request('/stories/homepage'),
    surprise: () => request('/stories/surprise'),
  },
  chapters: {
    get: (id: number) => request(`/chapters/${id}`),
    create: (data: any) => request('/chapters', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/chapters/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/chapters/${id}`, { method: 'DELETE' }),
    bookmark: (id: number) => request(`/chapters/${id}/bookmark`, { method: 'POST' }),
    complete: (id: number) => request(`/chapters/${id}/complete`, { method: 'POST' }),
    reorder: (data: { storyId: number; chapters: { id: number; chapterNum: number }[] }) =>
      request('/chapters/reorder', { method: 'PUT', body: JSON.stringify(data) }),
  },
  reviews: {
    create: (data: { storyId: number; rating: number; body: string }) =>
      request('/reviews', { method: 'POST', body: JSON.stringify(data) }),
    upvote: (id: number) => request(`/reviews/${id}/upvote`, { method: 'POST' }),
  },
  comments: {
    create: (data: { chapterId: number; paragraphIndex: number; body: string }) =>
      request('/comments', { method: 'POST', body: JSON.stringify(data) }),
    getByChapter: (chapterId: number) => request(`/comments/chapter/${chapterId}`),
  },
  users: {
    profile: (username: string) => request(`/users/profile/${username}`),
    readingLists: () => request('/users/reading-lists'),
    notifications: () => request('/users/notifications'),
    unreadCount: () => request('/users/notifications/unread-count'),
    markNotificationsRead: (id?: number) =>
      request('/users/notifications/read', { method: 'PATCH', body: JSON.stringify(id ? { id } : {}) }),
    updateProfile: (data: any) => request('/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
    followAuthor: (authorId: number) =>
      request(`/users/${authorId}/follow`, { method: 'POST' }),
    bookmarks: () => request('/users/bookmarks'),
    addFavorite: (storyId: number) =>
      request('/users/favorites', { method: 'POST', body: JSON.stringify({ storyId }) }),
    removeFavorite: (storyId: number) =>
      request(`/users/favorites/${storyId}`, { method: 'DELETE' }),
  },
  chapterComments: {
    list: (chapterId: number, params?: { page?: number; sort?: string }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return request(`/chapter-comments/${chapterId}${qs}`);
    },
    create: (data: { chapterId: number; body: string; parentId?: number }) =>
      request('/chapter-comments', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/chapter-comments/${id}`, { method: 'DELETE' }),
  },
  dashboard: {
    stories: () => request('/dashboard/stories'),
    analytics: (storyId: number) => request(`/dashboard/analytics/${storyId}`),
    followers: (storyId: number) => request(`/dashboard/followers/${storyId}`),
    recentActivity: () => request('/dashboard/recent-activity'),
  },
  discover: {
    search: (params: Record<string, string>) => {
      const qs = '?' + new URLSearchParams(params).toString();
      return request(`/discover/search${qs}`);
    },
    genres: () => request('/discover/genres'),
    tags: () => request('/discover/tags'),
    related: (storyId: number) => request(`/discover/related/${storyId}`),
    newAuthors: () => request('/discover/new-authors'),
  },
  leaderboard: {
    weeklyFollowers: () => request('/leaderboard/weekly-followers'),
    allTime: () => request('/leaderboard/all-time'),
    debut: () => request('/leaderboard/debut'),
  },
  contests: {
    list: () => request('/contests'),
    current: () => request('/contests/current'),
    get: (id: number) => request(`/contests/${id}`),
    getEntry: (contestId: number, entryId: number) => request(`/contests/${contestId}/entry/${entryId}`),
    enter: (id: number, data: { title: string; content: string; genre: string }) =>
      request(`/contests/${id}/enter`, { method: 'POST', body: JSON.stringify(data) }),
    updateEntry: (id: number, data: { title: string; content: string; genre: string }) =>
      request(`/contests/${id}/entry`, { method: 'PUT', body: JSON.stringify(data) }),
    vote: (entryId: number) =>
      request(`/contests/entry/${entryId}/vote`, { method: 'POST' }),
    trackRead: (entryId: number, readTime: number) =>
      request(`/contests/entry/${entryId}/read`, { method: 'POST', body: JSON.stringify({ readTime }) }),
    pastWinners: () => request('/contests/past/winners'),
    create: (data: any) => request('/contests', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/contests/${id}/admin`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/contests/${id}/admin`, { method: 'DELETE' }),
  },
  admin: {
    stories: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/admin/stories${qs}`);
    },
    unpublish: (id: number, reason: string) =>
      request(`/admin/stories/${id}/unpublish`, { method: 'POST', body: JSON.stringify({ reason }) }),
    republish: (id: number) =>
      request(`/admin/stories/${id}/republish`, { method: 'POST' }),
    newMembers: () => request('/admin/new-members'),
  },
  penNames: {
    list: () => request('/pennames'),
    create: (data: { penName: string; bio?: string; avatarUrl?: string }) =>
      request('/pennames', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: { bio?: string; avatarUrl?: string }) =>
      request(`/pennames/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/pennames/${id}`, { method: 'DELETE' }),
    profile: (penName: string) => request(`/pennames/profile/${encodeURIComponent(penName)}`),
  },
  progress: {
    save: (data: { storyId: number; chapterId: number; scrollPosition: number }) =>
      request('/progress', { method: 'POST', body: JSON.stringify(data) }),
    continueReading: () => request('/progress/continue-reading'),
  },
  forums: {
    categories: () => request('/forums/categories'),
    category: (slug: string, params?: { page?: number }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return request(`/forums/categories/${encodeURIComponent(slug)}${qs}`);
    },
    thread: (id: number, params?: { page?: number }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return request(`/forums/threads/${id}${qs}`);
    },
    createThread: (data: { categoryId: number; title: string; body: string }) =>
      request('/forums/threads', { method: 'POST', body: JSON.stringify(data) }),
    createPost: (data: { threadId: number; body: string }) =>
      request('/forums/posts', { method: 'POST', body: JSON.stringify(data) }),
    updatePost: (id: number, data: { body: string }) =>
      request(`/forums/posts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePost: (id: number) => request(`/forums/posts/${id}`, { method: 'DELETE' }),
    updateThread: (id: number, data: { isPinned?: boolean; isLocked?: boolean }) =>
      request(`/forums/threads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },
  ink: {
    tip: (storyId: number, amount: number) =>
      request('/ink/tip', { method: 'POST', body: JSON.stringify({ storyId, amount }) }),
    balance: () => request('/ink/balance'),
    received: (userId: number) => request(`/ink/received/${userId}`),
  },
  upload: {
    cover: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem('scribehaven_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/upload/cover`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(data.error || 'Upload failed');
      }
      return res.json();
    },
    avatar: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem('scribehaven_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/upload/avatar`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(data.error || 'Upload failed');
      }
      return res.json();
    },
    delete: (url: string) =>
      request('/upload', { method: 'DELETE', body: JSON.stringify({ url }) }),
  },
  volumes: {
    list: (storyId: number) => request(`/volumes/story/${storyId}`),
    create: (data: { storyId: number; title: string; description?: string; sortOrder?: number }) =>
      request('/volumes', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: { title?: string; description?: string; sortOrder?: number }) =>
      request(`/volumes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/volumes/${id}`, { method: 'DELETE' }),
    assignChapter: (chapterId: number, volumeId: number | null) =>
      request(`/volumes/chapter/${chapterId}`, { method: 'PATCH', body: JSON.stringify({ volumeId }) }),
  },
  messages: {
    conversations: () => request('/messages/conversations'),
    conversation: (id: number, params?: { page?: number }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return request(`/messages/conversations/${id}${qs}`);
    },
    send: (data: { recipientId?: number; conversationId?: number; body: string }) =>
      request('/messages/send', { method: 'POST', body: JSON.stringify(data) }),
    unreadCount: () => request('/messages/unread-count'),
    start: (username: string) =>
      request('/messages/start', { method: 'POST', body: JSON.stringify({ username }) }),
  },
};
